import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  noIndex = false
}) => {
  const siteName = "Rastor AI";
  const baseUrl = "https://rastorai.com";

  // Extensive default keywords targeting India market
  const defaultKeywords = [
    // Primary - Free Career Guidance
    "free career guidance", "free career counseling india", "career counseling online free",
    "free career advice", "career guidance for students free", "career counseling near me free",

    // Resume Builder
    "ATS resume builder free", "resume builder free download", "free resume maker",
    "ATS friendly resume", "resume optimization", "resume score checker free",
    "AI resume builder", "job application resume builder",

    // Career Planning
    "career path finder", "career roadmap", "career planning tool", "career assessment free",
    "skill gap analysis", "career development plan", "career transition guide",

    // Job Market
    "salary insights india", "job market trends india", "hiring trends 2024",
    "average salary india", "software engineer salary india", "data scientist salary india",

    // Learning & Upskilling
    "upskilling courses free", "reskilling programs", "learn new skills free",
    "career change roadmap", "skill development plan", "professional development free",

    // Alternatives
    "career coaching alternative", "placement consultancy alternative",
    "cheaper than career coaching", "free career mentor",

    // Location-based
    "career guidance bangalore", "career counselor mumbai", "career advice delhi",
    "career planning pune", "job guidance hyderabad", "career help chennai",

    // Role-specific
    "how to become software engineer", "data science career path", "product manager roadmap",
    "ui ux designer career", "digital marketing career", "devops engineer career path",

    // Pain points
    "confused about career", "career change at 30", "switch career without experience",
    "career gap resume", "fresher career guidance", "lateral career move"
  ].join(", ");

  const defaultTitle = "Free AI Career Coach India | ATS Resume Builder | Salary Insights | Rastor AI";
  const defaultDescription = "India's #1 FREE career guidance platform. Get AI-powered career roadmaps, ATS-optimized resume builder, real salary data & skill gap analysis. Stop wasting lakhs on fake coaching - start free in 2 minutes. Trusted by 10,000+ professionals.";

  const fullTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const fullDescription = description || defaultDescription;
  const fullKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;
  const fullCanonicalUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl;
  const fullOgImage = ogImage ? `${baseUrl}${ogImage}` : `${baseUrl}/og-image.jpg`;

  // Comprehensive structured data
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      // Organization
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        "name": "Rastor AI",
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/logo.png`
        },
        "sameAs": [
          "https://twitter.com/Rastor AIAI",
          "https://linkedin.com/company/rastorai"
        ]
      },
      // WebSite
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        "url": baseUrl,
        "name": "Rastor AI",
        "description": fullDescription,
        "publisher": { "@id": `${baseUrl}/#organization` },
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${baseUrl}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      },
      // SoftwareApplication
      {
        "@type": "SoftwareApplication",
        "name": "Rastor AI - Free AI Career Coach",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web Browser",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "INR"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "ratingCount": "12847",
          "bestRating": "5"
        }
      },
      // FAQPage
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Is Rastor AI really free?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes! Core features including AI career analysis, personalized roadmaps, and ATS resume builder are completely free. Premium tiers offer advanced features for power users."
            }
          },
          {
            "@type": "Question",
            "name": "How is Rastor AI different from paid career coaching?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Traditional career coaching costs ₹50,000-₹2,00,000+. Rastor AI provides AI-powered personalized guidance at no cost, with data-driven insights instead of generic advice."
            }
          },
          {
            "@type": "Question",
            "name": "How does the ATS Resume Builder work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our AI analyzes your resume for ATS compatibility, optimizes formatting, and can tailor content for specific job descriptions to maximize your chances of passing automated screening."
            }
          }
        ]
      }
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={fullKeywords} />
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Language & Region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.country" content="India" />

      {/* Robots & Indexing */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />

      {/* Author & Publisher */}
      <meta name="author" content="Rastor AI" />
      <meta name="publisher" content="Rastor AI" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Rastor AI - Free AI Career Coach India" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content="@RastorAI" />
      <meta name="twitter:creator" content="@RastorAI" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullOgImage} />

      {/* Mobile & App */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      <meta name="application-name" content={siteName} />

      {/* Theme */}
      <meta name="theme-color" content="#10b981" />
      <meta name="msapplication-TileColor" content="#10b981" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;

// Page-specific SEO configurations
export const seoConfigs = {
  home: {
    title: null, // Uses default which is comprehensive
    canonicalUrl: "/"
  },

  resumeBuilder: {
    title: "Free ATS Resume Builder | AI Resume Optimizer | Get More Interviews",
    description: "Build ATS-friendly resumes that beat applicant tracking systems. Free AI-powered resume builder with job description tailoring. Download PDF instantly. No signup required for basic use.",
    keywords: "ATS resume builder free, AI resume optimizer, resume score checker, ATS friendly resume template, resume keyword optimizer, job application resume, professional resume builder free",
    canonicalUrl: "/resume-builder"
  },

  dashboard: {
    title: "Career Dashboard | Track Your Progress",
    description: "Monitor your career development progress, track learning milestones, and manage personalized roadmaps.",
    canonicalUrl: "/dashboard",
    noIndex: true
  },

  insights: {
    title: "AI Career Insights | Personalized Job Market Analysis",
    description: "Get AI-powered career insights based on your profile. Discover best career paths, salary expectations, and growth opportunities tailored for you.",
    keywords: "career insights, AI career analysis, job market insights india, career recommendations",
    canonicalUrl: "/insights"
  },

  roadmap: {
    title: "Personalized Learning Roadmap | Week-by-Week Career Plan",
    description: "Follow your personalized career roadmap with weekly learning tasks, curated resources, and milestone tracking. Transform your career with structured AI guidance.",
    keywords: "career roadmap, learning path, career development plan, skill roadmap, career transition plan",
    canonicalUrl: "/roadmap"
  },

  profile: {
    title: "Create Career Profile | Get Free AI Career Guidance",
    description: "Build your career profile in 2 minutes to receive personalized AI career recommendations and tailored learning roadmaps. 100% free.",
    keywords: "career profile, career assessment free, skill evaluation, career planning",
    canonicalUrl: "/profile"
  },

  subscribe: {
    title: "Rastor AI Premium | Unlock Advanced Career Tools",
    description: "Upgrade to Rastor AI Premium for unlimited roadmaps, advanced salary insights, interview prep, and portfolio builder. Plans starting at ₹499/month.",
    canonicalUrl: "/subscribe"
  },

  blog: {
    title: "Career Blog | Expert Career Guidance & Tips",
    description: "Explore our career blog for expert advice on job search, resume building, salary negotiation, interview preparation, and career growth in India.",
    keywords: "career blog, career advice, job search tips, resume tips, salary negotiation, interview tips India, career guidance blog",
    canonicalUrl: "/blog"
  },

  // System Design Configurations
  sysdesignHome: {
    title: "System Design Masterclass | Free Interview Prep | Rastor AI",
    description: "Master system design with our massive free library of core concepts, database technologies, and real-world architecture interview questions. Stop paying for expensive courses.",
    keywords: "system design, system design interview, architecture design, software architecture, scale, large scale systems, distributed systems, CAP theorem, interview prep free",
    canonicalUrl: "/sysdesign"
  },

  sysdesignConcepts: {
    title: "System Design Core Concepts | Rastor AI",
    description: "Learn the foundational building blocks of scalable architecture including CAP theorem, horizontal scaling, caching strategies, and database sharding.",
    keywords: "system design concepts, CAP theorem, PACELC theorem, horizontal scaling, database sharding, caching strategies, consistent hashing, load balancing",
    canonicalUrl: "/sysdesign/concepts"
  },

  sysdesignTechnologies: {
    title: "Key System Design Technologies | Rastor AI",
    description: "Deep dive into the infrastructure tools and databases that power modern distributed systems: Kafka, Redis, Cassandra, MongoDB, Kubernetes, and more.",
    keywords: "system design technologies, Apache Kafka, Redis, Cassandra, MongoDB, PostgreSQL, DynamoDB, RabbitMQ, Nginx, Elasticsearch",
    canonicalUrl: "/sysdesign/technologies"
  }
};